import {
	getApiActionsForGivenModule,
	getApiActionsWithRequestedPayloadForGivenModule,
	getSideEffectActionsForGivenModule,
} from 'amc-common-components';
import {createAction, props} from '@ngrx/store';
import {
	ContentCode,
	GroupingMode,
	Inventory,
	InventoryFilters,
	InventorySorting,
	Language,
	MediaType,
	ModifyPinnedInventory,
	ProjectCode,
	WorkflowStatus,
	RightsInventoryBuffer,
	RightsSourcesOption,
	RightsView,
	Territory,
	View,
	RightsLanguageMetadata,
	Channel,
	Genre,
} from '@entities/rights/rights.interfaces';
import {StoreModules} from '@shared/enums/store';
import {getModuleActionName} from '@shared/helpers/store/actions.helper';

/**
 * Auxiliary Module name variable for creating unique actions.
 */
const moduleName = StoreModules.Rights;
/**
 * Auxiliary function for get the module action name.
 */
const createActionName = getModuleActionName(moduleName);
/**
 * Auxiliary variable for creating actions to load data.
 */
const getApiActions = getApiActionsForGivenModule(moduleName);
/**
 * Auxiliary variable for creating actions to load data with payload.
 */
const getApiActionsWithRequestedPayload = getApiActionsWithRequestedPayloadForGivenModule(moduleName);
/**
 * Auxiliary variable for creating side effects actions.
 */
const getSideEffectActions = getSideEffectActionsForGivenModule(moduleName);
/**
 * Auxiliary variable for creating actions to load data with payload.
 */
const getApiParamsActions = getApiActionsWithRequestedPayloadForGivenModule(moduleName);

/**
 * Auxiliary variable with declaration type of actions for Dashboard Actions Page.
 */
const types = {
	getRightsSources: createActionName('Rights Sources'),
	selectSource: createActionName('Select source'),
	getInventory: createActionName('Get inventory'),
	getInventoryXLS: createActionName('Get inventory XLS'),
	toggleInventoryXLSSelection: createActionName('Toggle inventory XLS selection'),
	removeInventoryXLSSelection: createActionName('Remove inventory XLS selection'),
	cancelInventoryLoading: createActionName('Cancel inventory loading'),
	setInventoryLastPage: createActionName('Set inventory last page'),
	getMediaTypes: createActionName('Get media types'),
	getTerritories: createActionName('Get territories'),
	getLanguages: createActionName('Get languages'),
	getChannels: createActionName('Get channels'),
	getGenres: createActionName('Get genres'),
	getLanguagesMetadata: createActionName('Get languages metadata'),
	getBrands: createActionName('Get brands'),
	getProjectTypes: createActionName('Get project types'),
	getProductOwners: createActionName('Get product owners'),
	getWorkflowStatuses: createActionName('Get workflow statuses'),
	setInventoryGrouping: createActionName('Set inventory grouping'),
	setInventoryFilters: createActionName('Set inventory filters'),
	setInventorySorting: createActionName('Set inventory sorting'),
	setInventoryView: createActionName('Set inventory view'),
	resetPinnedInventory: createActionName('Reset pinned inventory'),
	resetSearchResults: createActionName('Reset search results'),
	setInventoryPinned: createActionName('Set inventory pinned'),
	removeInventoryPinned: createActionName('Remove inventory pinned'),
	modifyInventoryPinned: createActionName('Modify inventory pinned'),
	copyInventoryToBuffer: 'Copy inventory to buffer',
	removeInventoryFromBuffer: 'Remove inventory from buffer',
	modifyInventoryInBuffer: 'Modify inventory in buffer',
	setRightsView: createActionName('Set rights view'),
	getPagesCount: createActionName('Pages Count'),
	selectPage: createActionName('Select Page'),
	resetState: createActionName('Reset state'),
};

/**
 * Auxiliary export declaration actions for Dashboard Actions Page.
 */
export const rightsActions = {
	rightsSources: getApiActions(types.getRightsSources, props<{sources: RightsSourcesOption[]}>()),
	source: createAction(types.selectSource, props<{source: RightsSourcesOption}>()),
	getRightsSources: getApiActions(types.getRightsSources, props<{sources: RightsSourcesOption[]}>()),
	selectSource: createAction(types.selectSource, props<{source: RightsSourcesOption}>()),
	getInventory: getApiParamsActions(types.getInventory, props<{page?: number}>(), props<{inventory: Inventory[]}>()),
	inventoryXLS: getApiActions(types.getInventoryXLS, props<{inventoryXLS: Inventory[]}>()),
	inventoryXLSSelection: getSideEffectActions(types.toggleInventoryXLSSelection, props<{key: string}>()),
	getInventoryXLS: getApiActions(types.getInventoryXLS, props<{inventoryXLS: Inventory[]}>()),
	toggleInventoryXLSSelection: getSideEffectActions(types.toggleInventoryXLSSelection, props<{key: string}>()),
	removeInventoryXLSSelection: getSideEffectActions(types.removeInventoryXLSSelection, props<{key: string}>()),
	cancelInventoryLoading: createAction(types.cancelInventoryLoading),
	getMediaTypes: getApiActions(types.getMediaTypes, props<{mediaTypes: MediaType[]}>()),
	getTerritories: getApiActions(types.getTerritories, props<{territories: Territory[]}>()),
	getLanguages: getApiActions(types.getLanguages, props<{languages: Language[]}>()),
	getChannels: getApiActions(types.getChannels, props<{channels: Channel[]}>()),
	getGenres: getApiActions(types.getGenres, props<{genres: Genre[]}>()),
	getLanguagesMetadata: getApiActions(
		types.getLanguagesMetadata,
		props<{data: Record<string, RightsLanguageMetadata[]>}>()
	),
	getBrands: getApiActions(types.getBrands, props<{brands: ContentCode[]}>()),
	getProjectTypes: getApiActions(types.getProjectTypes, props<{projectTypes: ProjectCode[]}>()),
	getProductOwners: getApiActions(types.getProductOwners, props<{productOwners: ProjectCode[]}>()),
	getWorkflowStatuses: getApiActions(types.getWorkflowStatuses, props<{workflowStatuses: WorkflowStatus[]}>()),
	setInventoryGrouping: createAction(types.setInventoryGrouping, props<{grouping: GroupingMode}>()),
	setInventoryFilters: createAction(types.setInventoryFilters, props<{filters: InventoryFilters}>()),
	setInventorySorting: createAction(types.setInventorySorting, props<{sorting: InventorySorting}>()),
	setInventoryView: createAction(types.setInventoryView, props<{view: View}>()),
	resetPinnedInventory: createAction(types.resetPinnedInventory),
	resetSearchResults: createAction(types.resetSearchResults),
	setInventoryPinned: getApiActions(types.setInventoryPinned, props<{buffer: RightsInventoryBuffer}>()),
	removeInventoryPinned: getApiActionsWithRequestedPayload(
		types.removeInventoryPinned,
		props<{query: string}>(),
		props<{buffer: RightsInventoryBuffer}>()
	),
	copyInventoryToBuffer: getApiActions(types.copyInventoryToBuffer, props<{buffer: RightsInventoryBuffer}>()),
	removeInventoryFromBuffer: getApiParamsActions(
		types.removeInventoryFromBuffer,
		props<{query: string}>(),
		props<{buffer: RightsInventoryBuffer}>()
	),
	modifyInventoryInBuffer: getApiParamsActions(
		types.modifyInventoryInBuffer,
		props<{mod: ModifyPinnedInventory}>(),
		props<{buffer: RightsInventoryBuffer}>()
	),
	setRightsView: createAction(types.setRightsView, props<{rights: RightsView}>()),
	getPagesCount: getApiActions(types.getPagesCount, props<{pagesCount: number}>()),
	selectPage: createAction(types.selectPage, props<{page: number}>()),
	resetState: createAction(types.resetState),
};
